import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FiRefreshCw, FiPlus, FiLoader, FiCheckCircle, FiAlertCircle, FiClock } from 'react-icons/fi';

function normalizeStatus(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'ACTIVE') return 'APPROVED';
  if (s === 'IN_REVIEW' || s === 'PENDING') return 'IN REVIEW';
  return s || 'UNKNOWN';
}

function statusBadgeClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'APPROVED' || s === 'ACTIVE') return 'bg-emerald-100 text-emerald-700';
  if (s === 'REJECTED' || s === 'DISABLED' || s === 'PAUSED') return 'bg-red-100 text-red-700';
  if (s === 'IN_REVIEW' || s === 'PENDING') return 'bg-amber-100 text-amber-700';
  return 'bg-neutral-100 text-neutral-600';
}

function statusIcon(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'APPROVED' || s === 'ACTIVE') return <FiCheckCircle className="w-4 h-4" />;
  if (s === 'REJECTED' || s === 'DISABLED' || s === 'PAUSED') return <FiAlertCircle className="w-4 h-4" />;
  return <FiClock className="w-4 h-4" />;
}

function detectVariables(text) {
  const matches = text.match(/\{\{\d+\}\}/g) || [];
  return [...new Set(matches)].sort((a, b) => Number(a.replace(/\D/g, '')) - Number(b.replace(/\D/g, '')));
}

export default function WhatsAppMetaTemplateManager({ senderId, onSync }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('en_US');
  const [category, setCategory] = useState('UTILITY');
  const [body, setBody] = useState('');
  const [samples, setSamples] = useState({});

  const variables = useMemo(() => detectVariables(body), [body]);

  useEffect(() => {
    setSamples((prev) => {
      const next = {};
      variables.forEach((v) => {
        next[v] = prev[v] || '';
      });
      return next;
    });
  }, [variables]);

  const load = async () => {
    setLoading(true);
    try {
      const params = senderId ? { sender_id: senderId } : {};
      const { data } = await axios.get('/api/whatsapp/meta-templates', { params });
      setTemplates(Array.isArray(data.templates) ? data.templates : []);
    } catch (e) {
      console.error('Error loading meta templates:', e);
    } finally {
      setLoading(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post('/api/whatsapp/meta-templates/sync', { sender_id: senderId });
      setTemplates(Array.isArray(data.templates) ? data.templates : []);
      if (onSync) await onSync();
    } catch (e) {
      console.error('Error syncing meta templates:', e);
    } finally {
      setSyncing(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim() || !body.trim()) return;
    const bodyComponent = {
      type: 'BODY',
      text: body.trim(),
    };
    if (variables.length > 0) {
      bodyComponent.example = {
        body_text: [variables.map((v) => samples[v] || 'sample')],
      };
    }
    const components = [bodyComponent];
    setCreating(true);
    try {
      await axios.post('/api/whatsapp/meta-templates', {
        sender_id: senderId,
        name: name.trim(),
        language: language.trim(),
        category,
        components,
      });
      setShowForm(false);
      setName('');
      setBody('');
      setSamples({});
      await sync();
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Template creation failed';
      alert(`Error: ${msg}`);
      console.error('Error creating meta template:', e);
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    // Auto-sync from Meta on load so status is always current
    sync().catch(() => load());
  }, [senderId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mt-6 pt-6 border-t border-neutral-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-neutral-900">Meta Templates</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={sync}
            disabled={syncing}
            className="px-3 py-1.5 bg-white border border-neutral-300 text-neutral-700 rounded-lg text-xs font-semibold hover:bg-neutral-50 disabled:opacity-50 flex items-center gap-1"
          >
            {syncing ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiRefreshCw className="w-3 h-3" />}
            Sync
          </button>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 flex items-center gap-1"
          >
            <FiPlus className="w-3 h-3" />
            Create
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={create} className="mb-4 space-y-3 bg-neutral-50 rounded-lg p-3 border border-neutral-200">
          <div>
            <label className="block text-xs font-semibold text-neutral-700 mb-1">Template name</label>
            <input
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. new_reply_notification"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-neutral-700 mb-1">Language</label>
              <input
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                placeholder="en_US"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-700 mb-1">Category</label>
              <select
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="UTILITY">UTILITY</option>
                <option value="MARKETING">MARKETING</option>
                <option value="AUTHENTICATION">AUTHENTICATION</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-700 mb-1">Body</label>
            <textarea
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Hello {{1}}, your order {{2}} is ready."
              rows={3}
              required
            />
            <p className="text-[10px] text-neutral-500 mt-1">
              Use {'{{1}}'}, {'{{2}}'} etc. for variables. Meta requires the message to be long enough for the number of variables.
            </p>
          </div>
          {variables.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-neutral-700">Sample values for Meta review</p>
              {variables.map((v) => (
                <div key={v} className="flex items-center gap-2">
                  <span className="text-xs font-mono text-neutral-600 w-10">{v}</span>
                  <input
                    className="flex-1 border border-neutral-200 rounded-lg px-3 py-1.5 text-sm bg-white"
                    value={samples[v] || ''}
                    onChange={(e) => setSamples((s) => ({ ...s, [v]: e.target.value }))}
                    placeholder={`Sample for ${v}`}
                    required
                  />
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={creating}
              className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {creating ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPlus className="w-4 h-4" />}
              Submit to Meta
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 bg-white border border-neutral-300 text-neutral-700 rounded-lg text-sm font-semibold hover:bg-neutral-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-neutral-500 flex items-center gap-2">
          <FiLoader className="w-4 h-4 animate-spin" /> Loading templates...
        </div>
      ) : templates.length === 0 ? (
        <p className="text-sm text-neutral-500">No synced templates. Click Sync to fetch from Meta.</p>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {templates.map((t) => (
            <div key={`${t.name}-${t.language}`} className="border border-neutral-200 rounded-lg p-3 bg-white">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-neutral-900 truncate">{t.name}</p>
                  <p className="text-xs text-neutral-500">{t.language} · {t.category || '—'}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${statusBadgeClass(t.status)}`}>
                  {statusIcon(t.status)}
                  {normalizeStatus(t.status)}
                </span>
              </div>
              {t.rejection_reason && (
                <p className="mt-2 text-xs text-red-600">{t.rejection_reason}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
